import { TypographyColors, TableCellTypes } from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { INCOMING_SHIPMENT_SORT_FIELDS } from './incomingShipments.constants';
import {
  CellText,
  makeLinkCell,
  makeUnitTypeCell,
  makeUnitScopedCell,
  makeDiscrepanciesCell,
} from '@/components/common';
import { buildShipmentStatusVariants } from '@/config/statuses';
import { SHIPMENT_STATUS_TONE } from '@/config/statusTones';
import { unitTypeLabel, buildRowUnitOptions } from './incomingShipmentUnits';

// Below which "Days left" turns red.
const URGENT_DAYS = 7;

const renderUnitTypeCell = makeUnitTypeCell((t, row) => buildRowUnitOptions(t, row.byUnit));

// Days left: red under a week, dash when not applicable.
const DaysLeftCell = ({ value }) => {
  const t = useTranslations();
  if (value == null) return <CellText color={TypographyColors.secondary}>-</CellText>;
  const color = value < URGENT_DAYS ? TypographyColors.error : TypographyColors.primary;
  return <CellText color={color}>{t('INCOMING_SHIPMENTS_DAYS_LEFT', { days: value })}</CellText>;
};
const renderDaysLeft = (ctx) => <DaysLeftCell value={ctx.value} />;

const canSort = (id) => Boolean(INCOMING_SHIPMENT_SORT_FIELDS[id]);

// The checkbox and Action column are added by the Table itself.
export const buildIncomingShipmentsColumns = (
  t,
  { unitHeaderSelect, onOpenDiscrepancy, onOpenShipment },
) =>
  [
    {
      id: 'shipmentId',
      header: t('FIELD_SHIPMENT_ID'),
      type: TableCellTypes.custom,
      width: 140,
      pinnable: true,
      cell: makeLinkCell(onOpenShipment),
    },
    {
      id: 'materialName',
      header: t('FIELD_MATERIAL_NAME'),
      type: TableCellTypes.text,
      width: 200,
      pinnable: true,
    },
    {
      id: 'totalContractsLinked',
      header: t('FIELD_TOTAL_CONTRACTS_LINKED'),
      type: TableCellTypes.text,
      width: 160,
      pinnable: true,
    },
    {
      id: 'totalDeclarationsReceived',
      header: t('INCOMING_SHIPMENTS_COL_TOTAL_DECLARATIONS_RECEIVED'),
      type: TableCellTypes.text,
      width: 180,
      pinnable: true,
    },
    {
      id: 'supplierName',
      header: t('FIELD_SUPPLIER_NAME'),
      type: TableCellTypes.text,
      width: 150,
      pinnable: true,
    },
    {
      id: 'status',
      header: t('FIELD_STATUS'),
      type: TableCellTypes.statusDot,
      width: 160,
      pinnable: true,
      variantMap: buildShipmentStatusVariants(t, SHIPMENT_STATUS_TONE),
    },
    {
      id: 'facilityName',
      header: t('FIELD_FACILITY_NAME'),
      type: TableCellTypes.text,
      width: 143,
      pinnable: true,
    },
    {
      id: 'terminal',
      header: t('FIELD_TERMINAL'),
      type: TableCellTypes.text,
      width: 141,
      pinnable: true,
    },
    {
      id: 'vessel',
      header: t('FIELD_VESSEL'),
      type: TableCellTypes.text,
      width: 141,
      pinnable: true,
    },
    {
      id: 'unit',
      header: t('FIELD_UNIT'),
      type: TableCellTypes.custom,
      width: 100,
      pinnable: true,
      cell: makeUnitScopedCell('unit'),
    },
    {
      id: 'unitType',
      header: t('FIELD_UNIT_TYPE', {
        unit: unitTypeLabel(t, unitHeaderSelect.value),
      }),
      type: TableCellTypes.custom,
      width: 252,
      pinnable: true,
      cell: renderUnitTypeCell,
      headerSelect: unitHeaderSelect,
    },
    {
      id: 'shipmentQuantity',
      header: t('FIELD_SHIPMENT_QUANTITY'),
      type: TableCellTypes.custom,
      width: 200,
      pinnable: true,
      cell: makeUnitScopedCell('shipmentQuantity'),
    },
    {
      id: 'receivedDeclarationQuantity',
      header: t('FIELD_RECEIVED_DECLARATION_QUANTITY'),
      type: TableCellTypes.custom,
      width: 200,
      pinnable: true,
      cell: makeUnitScopedCell('receivedDeclarationQuantity'),
    },
    {
      id: 'pendingQuantity',
      header: t('FIELD_PENDING_QUANTITY'),
      type: TableCellTypes.custom,
      width: 200,
      pinnable: true,
      cell: makeUnitScopedCell('pendingQuantity'),
    },
    {
      id: 'availableQuantity',
      header: t('FIELD_AVAILABLE_QUANTITY'),
      type: TableCellTypes.custom,
      width: 200,
      pinnable: true,
      cell: makeUnitScopedCell('availableQuantity'),
    },
    {
      id: 'ghgEmissions',
      header: t('FIELD_GHG_EMISSIONS'),
      type: TableCellTypes.text,
      width: 200,
      pinnable: true,
    },
    {
      id: 'ghgEmissionSavings',
      header: t('FIELD_GHG_EMISSION_SAVINGS'),
      type: TableCellTypes.text,
      width: 200,
      pinnable: true,
    },
    {
      id: 'rawMaterialType',
      header: t('FIELD_RAW_MATERIAL_TYPE'),
      type: TableCellTypes.text,
      width: 200,
      pinnable: true,
    },
    {
      id: 'countryOfOrigin',
      header: t('FIELD_COUNTRY_OF_ORIGIN'),
      type: TableCellTypes.text,
      width: 200,
      pinnable: true,
    },
    {
      id: 'feedstockCategory',
      header: t('FIELD_FEEDSTOCK_CATEGORY'),
      type: TableCellTypes.text,
      width: 200,
      pinnable: true,
    },
    {
      id: 'allocationPreference',
      header: t('FIELD_ALLOCATION_PREFERENCE'),
      type: TableCellTypes.text,
      width: 200,
      pinnable: true,
    },
    {
      id: 'dateOfDischarge',
      header: t('FIELD_DATE_OF_DISCHARGE'),
      type: TableCellTypes.text,
      width: 150,
      pinnable: true,
    },
    {
      id: 'dateOfDispatch',
      header: t('FIELD_DATE_OF_DISPATCH'),
      type: TableCellTypes.text,
      width: 150,
      pinnable: true,
    },
    {
      id: 'daysLeft',
      header: t('FIELD_DAYS_LEFT'),
      type: TableCellTypes.custom,
      width: 150,
      pinnable: true,
      cell: renderDaysLeft,
    },
    {
      id: 'discrepancies',
      header: t('FIELD_DISCREPANCIES'),
      type: TableCellTypes.custom,
      width: 200,
      pinnable: true,
      cell: makeDiscrepanciesCell(onOpenDiscrepancy),
    },
  ].map((column) => ({ ...column, sortable: canSort(column.id) }));
